import React from 'react';
import { createPortal } from 'react-dom';
import type {
  AnnualTimetableCell,
  ProgramFixedContentRow,
  ProgramScopedContentRow,
  ProgramScopeType,
} from '../../types/domain';
import type { AnnualTimetableNaturalKey } from '../../services/annualTimetableService';
import { fetchProgramAuditSnapshotForProgramPage } from '../../services/programDataAuditService';
import { deleteProgramScopedContentRowsByIds } from '../../services/programScopedContentService';
import { deleteProgramFixedContentRowsByIds } from '../../services/programFixedContentService';
import { deleteEmptyPresentationBindingSelections } from '../../services/programEmptyPresentationBindingService';
import {
  deleteProgramProgressCellsAuditSelections,
  deletePresentationAuditSelections,
} from '../../services/programPresentationAuditService';
import {
  deleteEmptyWorksheetBindingSelections,
  deleteWorksheetAuditSelections,
} from '../../services/programWorksheetAuditService';
import ModalBodyLoadingSpinner from '../common/ModalBodyLoadingSpinner';
import PresentationAuditSection from './PresentationAuditSection';
import WorksheetAuditSection from './WorksheetAuditSection';
import type { ProgramProgressCell } from '../../types/domain';

type AuditColumnDef = {
  key: string;
  /** 표 헤더 — DB 컬럼명 그대로 */
  dbColumn: string;
  hint?: string;
  maxLen?: number;
  /** 행을 구분하는 식별 키(일정 슬롯·스코프·순서 등) */
  identity?: boolean;
};

function asAuditRows<T extends object>(rows: T[]): Record<string, unknown>[] {
  return rows as unknown as Record<string, unknown>[];
}

function formatAuditCellValue(value: unknown, _maxLen = 28): { display: string; title?: string } {
  if (value === null || value === undefined) return { display: '—' };
  if (typeof value === 'boolean') return { display: value ? 'true' : 'false' };
  if (typeof value === 'number') return { display: String(value) };
  if (typeof value === 'object') {
    const full = JSON.stringify(value);
    return { display: full, title: full };
  }
  const full = String(value);
  const trimmed = full.trim();
  if (!trimmed) return { display: '—' };
  return { display: full, title: full };
}

/** annual_timetable_cells — 프로그램 표 «일정» 열이 읽는 DB */
const ANNUAL_TIMETABLE_COLUMNS: AuditColumnDef[] = [
  { key: 'id', dbColumn: 'id', hint: 'PK·source_annual_cell_key', maxLen: 8, identity: true },
  { key: 'workspace_id', dbColumn: 'workspace_id', maxLen: 8 },
  { key: 'grade_id', dbColumn: 'grade_id', maxLen: 8 },
  { key: 'academic_year', dbColumn: 'academic_year', maxLen: 6 },
  { key: 'term_number', dbColumn: 'term_number', maxLen: 4 },
  { key: 'monday_iso', dbColumn: 'monday_iso', hint: '일정 주', identity: true, maxLen: 12 },
  { key: 'weekday_key', dbColumn: 'weekday_key', hint: '요일', identity: true, maxLen: 8 },
  { key: 'slot_index', dbColumn: 'slot_index', hint: '교시', identity: true, maxLen: 4 },
  { key: 'text_value', dbColumn: 'text_value', hint: '표 시간표명', maxLen: 24 },
  { key: 'memo', dbColumn: 'memo', maxLen: 20 },
  { key: 'color_hex', dbColumn: 'color_hex', maxLen: 10 },
  { key: 'slot_kind', dbColumn: 'slot_kind', maxLen: 12 },
  { key: 'is_editable', dbColumn: 'is_editable', maxLen: 6 },
  { key: 'is_dedicated', dbColumn: 'is_dedicated', maxLen: 6 },
  { key: 'is_base_excluded', dbColumn: 'is_base_excluded', maxLen: 6 },
  { key: 'created_at', dbColumn: 'created_at', maxLen: 18 },
  { key: 'updated_at', dbColumn: 'updated_at', maxLen: 18 },
];

/** program_scoped_content_rows */
const SCOPED_ROW_COLUMNS: AuditColumnDef[] = [
  { key: 'id', dbColumn: 'id', hint: 'PK', maxLen: 8, identity: true },
  { key: 'workspace_id', dbColumn: 'workspace_id', maxLen: 8 },
  { key: 'grade_id', dbColumn: 'grade_id', maxLen: 8 },
  { key: 'academic_year', dbColumn: 'academic_year', maxLen: 6 },
  { key: 'term_number', dbColumn: 'term_number', maxLen: 4 },
  { key: 'scope_type', dbColumn: 'scope_type', hint: 'track|project', identity: true, maxLen: 8 },
  { key: 'scope_key', dbColumn: 'scope_key', hint: '편제·프로젝트 키', identity: true, maxLen: 24 },
  { key: 'order_index', dbColumn: 'order_index', hint: '행 순서', identity: true, maxLen: 6 },
  { key: 'source_annual_cell_key', dbColumn: 'source_annual_cell_key', hint: '연간표 연결(선택)', maxLen: 28 },
  { key: 'timetable_text', dbColumn: 'timetable_text', maxLen: 20 },
  { key: 'unit_text', dbColumn: 'unit_text', maxLen: 20 },
  { key: 'learning_text', dbColumn: 'learning_text', hint: '학습 본문(평문)', maxLen: 24 },
  { key: 'learning_note', dbColumn: 'learning_note', hint: '학습 메모', maxLen: 20 },
  { key: 'presentation_ref_id', dbColumn: 'presentation_ref_id', hint: '프레젠테이션 ref', maxLen: 12 },
  { key: 'worksheet_ref_id', dbColumn: 'worksheet_ref_id', hint: '활동지 ref', maxLen: 12 },
  { key: 'page_text', dbColumn: 'page_text', maxLen: 16 },
  { key: 'assistant_page_text', dbColumn: 'assistant_page_text', maxLen: 16 },
  { key: 'current_period_text', dbColumn: 'current_period_text', maxLen: 12 },
  { key: 'total_period_text', dbColumn: 'total_period_text', maxLen: 12 },
  { key: 'material_text', dbColumn: 'material_text', maxLen: 16 },
  { key: 'record_cross_curricular_text', dbColumn: 'record_cross_curricular_text', maxLen: 18 },
  { key: 'record_memo_text', dbColumn: 'record_memo_text', maxLen: 18 },
  { key: 'record_evaluation_text', dbColumn: 'record_evaluation_text', maxLen: 18 },
  { key: 'record_class_text', dbColumn: 'record_class_text', maxLen: 18 },
  { key: 'record_member_group_id', dbColumn: 'record_member_group_id', maxLen: 12 },
  { key: 'bg_color_by_field', dbColumn: 'bg_color_by_field', maxLen: 16 },
  { key: 'created_at', dbColumn: 'created_at', maxLen: 18 },
  { key: 'updated_at', dbColumn: 'updated_at', maxLen: 18 },
];

/** program_fixed_content_rows */
const FIXED_ROW_COLUMNS: AuditColumnDef[] = [
  { key: 'id', dbColumn: 'id', hint: 'PK', maxLen: 8, identity: true },
  { key: 'workspace_id', dbColumn: 'workspace_id', maxLen: 8 },
  { key: 'grade_id', dbColumn: 'grade_id', maxLen: 8 },
  { key: 'academic_year', dbColumn: 'academic_year', maxLen: 6 },
  { key: 'term_number', dbColumn: 'term_number', maxLen: 4 },
  { key: 'order_index', dbColumn: 'order_index', hint: '행 순서', identity: true, maxLen: 6 },
  { key: 'source_annual_cell_key', dbColumn: 'source_annual_cell_key', maxLen: 28 },
  { key: 'event_name', dbColumn: 'event_name', maxLen: 20 },
  { key: 'timetable_text', dbColumn: 'timetable_text', maxLen: 20 },
  { key: 'unit_text', dbColumn: 'unit_text', maxLen: 20 },
  { key: 'learning_text', dbColumn: 'learning_text', hint: '학습 본문(평문)', maxLen: 24 },
  { key: 'learning_note', dbColumn: 'learning_note', hint: '학습 메모', maxLen: 20 },
  { key: 'presentation_ref_id', dbColumn: 'presentation_ref_id', hint: '프레젠테이션 ref', maxLen: 12 },
  { key: 'worksheet_ref_id', dbColumn: 'worksheet_ref_id', hint: '활동지 ref', maxLen: 12 },
  { key: 'page_text', dbColumn: 'page_text', maxLen: 16 },
  { key: 'assistant_page_text', dbColumn: 'assistant_page_text', maxLen: 16 },
  { key: 'current_period_text', dbColumn: 'current_period_text', maxLen: 12 },
  { key: 'total_period_text', dbColumn: 'total_period_text', maxLen: 12 },
  { key: 'material_text', dbColumn: 'material_text', maxLen: 16 },
  { key: 'record_cross_curricular_text', dbColumn: 'record_cross_curricular_text', maxLen: 18 },
  { key: 'record_memo_text', dbColumn: 'record_memo_text', maxLen: 18 },
  { key: 'record_evaluation_text', dbColumn: 'record_evaluation_text', maxLen: 18 },
  { key: 'record_class_text', dbColumn: 'record_class_text', maxLen: 18 },
  { key: 'bg_color_by_field', dbColumn: 'bg_color_by_field', maxLen: 16 },
  { key: 'created_at', dbColumn: 'created_at', maxLen: 18 },
  { key: 'updated_at', dbColumn: 'updated_at', maxLen: 18 },
];

const PROGRAM_PROGRESS_CELL_COLUMNS: AuditColumnDef[] = [
  { key: 'id', dbColumn: 'id', hint: 'PK', maxLen: 8, identity: true },
  { key: 'monday_iso', dbColumn: 'monday_iso', hint: '주', identity: true, maxLen: 12 },
  { key: 'weekday_key', dbColumn: 'weekday_key', hint: '요일', identity: true, maxLen: 8 },
  { key: 'slot_index', dbColumn: 'slot_index', hint: '교시', identity: true, maxLen: 4 },
  { key: 'timetable_text', dbColumn: 'timetable_text', maxLen: 20 },
  { key: 'learning_text', dbColumn: 'learning_text', hint: '학습 본문(평문)', maxLen: 24 },
  { key: 'learning_note', dbColumn: 'learning_note', hint: '학습 메모', maxLen: 20 },
  { key: 'presentation_ref_id', dbColumn: 'presentation_ref_id', hint: '프레젠테이션 ref', maxLen: 12 },
  { key: 'worksheet_ref_id', dbColumn: 'worksheet_ref_id', hint: '활동지 ref', maxLen: 12 },
  { key: 'updated_at', dbColumn: 'updated_at', maxLen: 18 },
];

function ProgramProgressCellAuditTable(props: {
  rows: ProgramProgressCell[];
  rowKeyPrefix: string;
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  const { rows, rowKeyPrefix, selected, onToggle } = props;
  return (
    <table className="cp-program-data-cleanup-table cp-program-data-cleanup-table--db">
      <thead>
        <tr>
          <th className="cp-program-data-cleanup-col-check" scope="col" aria-label="선택" />
          {PROGRAM_PROGRESS_CELL_COLUMNS.map((col) => (
            <th
              key={col.dbColumn}
              scope="col"
              className={
                col.identity
                  ? 'cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity'
                  : 'cp-program-data-cleanup-col-db'
              }
              title={col.hint ? `${col.dbColumn} — ${col.hint}` : col.dbColumn}
            >
              <code>{col.dbColumn}</code>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIdx) => {
          const id = String(row.id ?? '').trim();
          const key = id ? `${rowKeyPrefix}:${id}` : `${rowKeyPrefix}:row:${rowIdx}`;
          return (
            <tr key={key}>
              <td className="cp-program-data-cleanup-col-check">
                <input
                  type="checkbox"
                  checked={selected.has(key)}
                  onChange={() => onToggle(key)}
                  disabled={!id}
                  aria-label={`프로그램 진도셀 ${String(row.monday_iso ?? '')} ${String(row.weekday_key ?? '')}`}
                />
              </td>
              {PROGRAM_PROGRESS_CELL_COLUMNS.map((col) => {
                const { display, title } = formatAuditCellValue(
                  (row as unknown as Record<string, unknown>)[col.key],
                  col.maxLen ?? 28
                );
                return (
                  <td
                    key={col.dbColumn}
                    className={
                      col.identity
                        ? 'cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity'
                        : 'cp-program-data-cleanup-col-db'
                    }
                    title={title}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function AuditDbTable(props: {
  rows: Record<string, unknown>[];
  columns: AuditColumnDef[];
  rowKeyPrefix: string;
  selected: Set<string>;
  onToggle: (key: string) => void;
  rowAriaLabel: (row: Record<string, unknown>) => string;
  /** false면 조회 전용(연간 시간표 등 — 이 모달에서 삭제 불가) */
  selectable?: boolean;
}) {
  const { rows, columns, rowKeyPrefix, selected, onToggle, rowAriaLabel, selectable = true } = props;
  return (
    <table className="cp-program-data-cleanup-table cp-program-data-cleanup-table--db">
      <thead>
        <tr>
          {selectable ? <th className="cp-program-data-cleanup-col-check" scope="col" aria-label="선택" /> : null}
          {columns.map((col) => (
            <th
              key={col.dbColumn}
              scope="col"
              className={
                col.identity
                  ? 'cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity'
                  : 'cp-program-data-cleanup-col-db'
              }
              title={col.hint ? `${col.dbColumn} — ${col.hint}` : col.dbColumn}
            >
              <code>{col.dbColumn}</code>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIdx) => {
          const id = String(row.id ?? '').trim();
          const key = id ? `${rowKeyPrefix}:${id}` : `${rowKeyPrefix}:row:${rowIdx}`;
          return (
            <tr key={key}>
              {selectable ? (
                <td className="cp-program-data-cleanup-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(key)}
                    onChange={() => onToggle(key)}
                    disabled={!id}
                    aria-label={rowAriaLabel(row)}
                  />
                </td>
              ) : null}
              {columns.map((col) => {
                const { display, title } = formatAuditCellValue(row[col.key], col.maxLen ?? 28);
                return (
                  <td
                    key={col.dbColumn}
                    className={
                      col.identity
                        ? 'cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity'
                        : 'cp-program-data-cleanup-col-db'
                    }
                    title={title}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export type ProgramDataCleanupModalProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  /** 현재 표에 보이는 일정 슬롯 좌표(연간 시간표 셀 조회용) */
  scheduleSlotKeys: AnnualTimetableNaturalKey[];
  /** 편제·프로젝트: 해당 스코프의 program_scoped_content_rows만 (고정 페이지는 생략) */
  scopeType?: ProgramScopeType;
  scopeKey?: string;
  /** 고정 프로그램 페이지에서만 true — program_fixed_content_rows 포함 */
  includeFixedRows?: boolean;
  /** 삭제 후 부모에서 데이터 재조회 */
  onCleanupComplete?: () => void;
};

const ProgramDataCleanupModal: React.FC<ProgramDataCleanupModalProps> = ({
  open,
  onClose,
  workspaceId,
  academicYear,
  termNumber,
  gradeId,
  scheduleSlotKeys,
  scopeType,
  scopeKey,
  includeFixedRows = false,
  onCleanupComplete,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [snapshot, setSnapshot] = React.useState<Awaited<ReturnType<typeof fetchProgramAuditSnapshotForProgramPage>> | null>(null);
  const [selected, setSelected] = React.useState<Set<string>>(() => new Set());
  const [deleting, setDeleting] = React.useState(false);

  React.useLayoutEffect(() => {
    if (open && gradeId.trim()) setLoading(true);
  }, [open, gradeId]);

  React.useEffect(() => {
    if (!open || !gradeId.trim()) {
      if (!open) setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(new Set());
    void (async () => {
      try {
        const data = await fetchProgramAuditSnapshotForProgramPage({
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
          scheduleSlotKeys,
          scopeType,
          scopeKey,
          includeFixedRows,
        });
        if (!cancelled) setSnapshot(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, academicYear, termNumber, gradeId, scheduleSlotKeys, scopeType, scopeKey, includeFixedRows]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectSectionKeys = (keys: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      keys.forEach((k) => {
        if (checked) next.add(k);
        else next.delete(k);
      });
      return next;
    });
  };

  const handleDelete = async () => {
    if (!snapshot || selected.size === 0) return;
    const scIds: string[] = [];
    const fxIds: string[] = [];
    const pcIds: string[] = [];
    const slideKeys: string[] = [];
    const emptyRefKeys: string[] = [];
    const worksheetKeys: string[] = [];
    const emptyWorksheetRefKeys: string[] = [];
    selected.forEach((k) => {
      if (k.startsWith('sc:')) scIds.push(k.slice(3));
      else if (k.startsWith('fx:')) fxIds.push(k.slice(3));
      else if (k.startsWith('pc:')) pcIds.push(k.slice(3));
      else if (k.startsWith('slide:')) slideKeys.push(k);
      else if (k.startsWith('empty-ref:')) emptyRefKeys.push(k);
      else if (k.startsWith('worksheet:')) worksheetKeys.push(k);
      else if (k.startsWith('empty-worksheet-ref:')) emptyWorksheetRefKeys.push(k);
    });
    const msg = `선택한 ${selected.size}건을 DB에서 삭제합니다. 프로그램 진도셀·슬라이드·활동지 삭제 시 연결 문서도 함께 지워지며, 빈 프레젠테이션·활동지 연결은 ref만 제거합니다. 계속할까요?`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    setError(null);
    try {
      if (emptyRefKeys.length) {
        await deleteEmptyPresentationBindingSelections({
          workspaceId,
          selectionKeys: emptyRefKeys,
          scopedRows: snapshot.scopedRows,
          fixedRows: snapshot.fixedRows,
          programProgressCells: snapshot.programProgressCells,
        });
      }
      if (emptyWorksheetRefKeys.length) {
        await deleteEmptyWorksheetBindingSelections({
          workspaceId,
          selectionKeys: emptyWorksheetRefKeys,
          scopedRows: snapshot.scopedRows,
          fixedRows: snapshot.fixedRows,
          programProgressCells: snapshot.programProgressCells,
        });
      }
      if (slideKeys.length) {
        await deletePresentationAuditSelections({ workspaceId, selectionKeys: slideKeys });
      }
      if (worksheetKeys.length) {
        await deleteWorksheetAuditSelections({ workspaceId, selectionKeys: worksheetKeys });
      }
      if (pcIds.length) {
        await deleteProgramProgressCellsAuditSelections({ workspaceId, cellIds: pcIds });
      }
      if (scIds.length) await deleteProgramScopedContentRowsByIds(scIds);
      if (fxIds.length) await deleteProgramFixedContentRowsByIds(fxIds);
      onCleanupComplete?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  if (!open) return null;

  const showScopedSection = !!(scopeType && (scopeKey ?? '').trim());
  const showFixedSection = includeFixedRows;

  const scKeys = (snapshot?.scopedRows ?? []).filter((r) => (r.id ?? '').trim()).map((r) => `sc:${r.id}`);
  const fxKeys = (snapshot?.fixedRows ?? []).filter((r) => (r.id ?? '').trim()).map((r) => `fx:${r.id}`);
  const pcKeys = (snapshot?.programProgressCells ?? []).filter((r) => (r.id ?? '').trim()).map((r) => `pc:${r.id}`);

  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget && !deleting) onClose();
      }}
    >
      <div
        className="cp-modal cp-program-data-cleanup-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-program-data-cleanup-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-program-data-cleanup-title">프로그램 데이터 정리</h3>
        <p className="page-desc cp-program-data-cleanup-intro">
          각 표는 DB 테이블의 <strong>실제 컬럼명</strong>을 헤더로 씁니다(긴 값은 표 가로 스크롤로 끝까지 탐색할 수 있습니다).
          <strong> 연간 시간표 셀</strong>(<code>annual_timetable_cells</code>)이 프로그램 표에 로드되는 <em>일정·시간표명</em>(
          <code>text_value</code>)의 DB 원본입니다.
          {showScopedSection ? (
            <>
              {' '}
              <strong>스코프 행</strong>(<code>program_scoped_content_rows</code>)은 편제·프로젝트 <em>프로그램 내용</em> 단위(
              <code>scope_type</code>·<code>scope_key</code>·<code>order_index</code>)이며, 일정과 별도로 유지됩니다.
              <code>source_annual_cell_key</code>는 연간표와 연결할 때만 채워집니다.
            </>
          ) : null}
          {showFixedSection ? (
            <>
              {' '}
              <strong>고정 행</strong>(<code>program_fixed_content_rows</code>)은 고정 프로그램 화면의 <code>order_index</code> 순 행입니다.
            </>
          ) : null}{' '}
          강조 열은 행을 구분하는 식별 키입니다. 연간 시간표는 조회 전용입니다. 이 모달은 현재 화면에 쓰이는 DB뿐 아니라{' '}
          <strong>UI에 바로 드러나지 않는 레거시 셀·슬라이드</strong>도 함께 보여줍니다. 편제·프로젝트는{' '}
          <strong>스코프 행</strong>·프레젠테이션 슬라이드·활동지를 선택해 삭제할 수 있습니다.
          {showFixedSection ? ' 고정 프로그램은 고정 행도 함께 정리할 수 있습니다.' : null}
        </p>
        {error ? (
          <p className="cp-program-data-cleanup-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="cp-modal-body cp-program-data-cleanup-body" aria-busy={loading || undefined}>
          {loading ? (
            <ModalBodyLoadingSpinner label="프로그램 데이터 정리 불러오는 중" />
          ) : snapshot ? (
            <>
              <section className="cp-program-data-cleanup-section">
                <div className="cp-program-data-cleanup-section-head">
                  <h4>연간 시간표 셀 — annual_timetable_cells ({snapshot.annualTimetableCells.length}건)</h4>
                </div>
                <p className="page-desc cp-program-data-cleanup-section-desc">
                  프로그램 표 «일정» 열에 보이는 데이터의 저장소(연간 시간표 메뉴). 유일 키: workspace_id + grade_id +
                  academic_year + term_number + monday_iso + weekday_key + slot_index. 화면 슬롯 {scheduleSlotKeys.length}
                  곳 조회 · DB 저장 {snapshot.annualTimetableCells.length}건
                  {scheduleSlotKeys.length > snapshot.annualTimetableCells.length
                    ? ' (일부 칸은 학사일정·템플릿만으로 표시되고 DB 행은 없을 수 있음)'
                    : ''}
                  . <code>text_value</code>가 표의 과목/시간표명입니다. 이 섹션은 조회 전용입니다.
                </p>
                <div className="cp-program-data-cleanup-table-wrap">
                  <AuditDbTable
                    rows={asAuditRows(snapshot.annualTimetableCells)}
                    columns={ANNUAL_TIMETABLE_COLUMNS}
                    rowKeyPrefix="at"
                    selected={selected}
                    onToggle={toggle}
                    selectable={false}
                    rowAriaLabel={(r) =>
                      `연간 시간표 ${String(r.monday_iso ?? '')} ${String(r.weekday_key ?? '')} ${String(r.slot_index ?? '')} ${String(r.text_value ?? '')}`
                    }
                  />
                </div>
              </section>

              {showScopedSection ? (
                <section className="cp-program-data-cleanup-section">
                  <div className="cp-program-data-cleanup-section-head">
                    <h4>스코프 행 — program_scoped_content_rows ({snapshot.scopedRows.length}건)</h4>
                    <button
                      type="button"
                      className="cp-btn-secondary cp-program-data-cleanup-select-all"
                      onClick={() =>
                        selectSectionKeys(scKeys, scKeys.some((k) => !selected.has(k)))
                      }
                    >
                      {scKeys.every((k) => selected.has(k)) ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                  <p className="page-desc cp-program-data-cleanup-section-desc">
                    프로그램 내용 본문·학습 메모·프레젠/활동지 ref(<code>learning_text</code>·<code>learning_note</code>·
                    <code>presentation_ref_id</code>·<code>worksheet_ref_id</code>)·범교과·메모·평가·수업기록은 이 테이블에
                    저장됩니다.
                  </p>
                  <div className="cp-program-data-cleanup-table-wrap">
                    <AuditDbTable
                      rows={asAuditRows(snapshot.scopedRows)}
                      columns={SCOPED_ROW_COLUMNS}
                      rowKeyPrefix="sc"
                      selected={selected}
                      onToggle={toggle}
                      rowAriaLabel={(r) =>
                        `스코프 행 ${String(r.scope_type ?? '')} ${String(r.scope_key ?? '')} #${String(r.order_index ?? '')}`
                      }
                    />
                  </div>
                </section>
              ) : null}

              {showFixedSection ? (
                <section className="cp-program-data-cleanup-section">
                  <div className="cp-program-data-cleanup-section-head">
                    <h4>고정 행 — program_fixed_content_rows ({snapshot.fixedRows.length}건)</h4>
                    <button
                      type="button"
                      className="cp-btn-secondary cp-program-data-cleanup-select-all"
                      onClick={() =>
                        selectSectionKeys(fxKeys, fxKeys.some((k) => !selected.has(k)))
                      }
                    >
                      {fxKeys.every((k) => selected.has(k)) ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                  <div className="cp-program-data-cleanup-table-wrap">
                    <AuditDbTable
                      rows={asAuditRows(snapshot.fixedRows)}
                      columns={FIXED_ROW_COLUMNS}
                      rowKeyPrefix="fx"
                      selected={selected}
                      onToggle={toggle}
                      rowAriaLabel={(r) => `고정 행 order_index ${String(r.order_index ?? '')}`}
                    />
                  </div>
                </section>
              ) : null}

              {snapshot.programProgressCells.length ? (
                <section className="cp-program-data-cleanup-section">
                  <div className="cp-program-data-cleanup-section-head">
                    <h4>프로그램 진도셀 — program_progress_cells ({snapshot.programProgressCells.length}건)</h4>
                    <button
                      type="button"
                      className="cp-btn-secondary cp-program-data-cleanup-select-all"
                      onClick={() => selectSectionKeys(pcKeys, pcKeys.some((k) => !selected.has(k)))}
                    >
                      {pcKeys.every((k) => selected.has(k)) ? '전체 해제' : '전체 선택'}
                    </button>
                  </div>
                  <p className="page-desc cp-program-data-cleanup-section-desc">
                    표 일정 슬롯에 대응하는 프로그램 진도셀입니다. 현재 UI가 직접 읽지 않는 레거시 셀일 수 있으므로, 연결된
                    슬라이드 여부를 확인한 뒤 정리하세요.
                  </p>
                  <div className="cp-program-data-cleanup-table-wrap">
                    <ProgramProgressCellAuditTable
                      rows={snapshot.programProgressCells}
                      rowKeyPrefix="pc"
                      selected={selected}
                      onToggle={toggle}
                    />
                  </div>
                </section>
              ) : null}

              <PresentationAuditSection
                entries={snapshot.presentationEntries}
                selected={selected}
                onToggle={toggle}
                onSelectSectionKeys={selectSectionKeys}
              />

              <WorksheetAuditSection
                entries={snapshot.worksheetEntries}
                selected={selected}
                onToggle={toggle}
                onSelectSectionKeys={selectSectionKeys}
              />
            </>
          ) : (
            <p className="page-desc">데이터가 없습니다.</p>
          )}
        </div>
        <div className="cp-modal-footer cp-program-data-cleanup-footer">
          <button type="button" className="cp-btn-secondary" onClick={() => onClose()} disabled={deleting}>
            닫기
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            disabled={!selected.size || deleting || loading}
            onClick={() => void handleDelete()}
          >
            {deleting ? '삭제 중…' : `선택 삭제 (${selected.size})`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProgramDataCleanupModal;
